import React from "react";
import { Helmet } from "react-helmet-async";
import { Block } from "../../atoms/Layout/Block/Block";
import { GridCell } from "../../atoms/Layout/GridCell/GridCell";
import { GridRow } from "../../atoms/Layout/GridRow/GridRow";
import { H1, H3 } from "../../atoms/Text/Headings";
import { Text } from "../../atoms/Text/Text";
import { Pages } from "../../constants/Pages";
import { PagesSEO } from "../../constants/PagesSEO";
import { Freshdesk } from "../../molecules/Freshdesk/Freshdesk";
import { isServer } from "../../../utils";
import { RightRail } from "../../molecules/RightRail/RightRail";
import { DesktopQuery } from "../../atoms/Layout/Responsive";
import styles from "./Help.css";
import { BadgeService } from "../../services/BadgeService";
import { environment } from "../../config/environment";
import { helpPageStructuredData } from "../../constants/SchemasSEO";

export class HelpTemplate extends React.PureComponent {
  state = {
    url: "",
  };

  goTo = (id) => () => {
    const isAarpHeaderHovered = document.getElementById("root").offsetTop !== 0;

    if (window.location.hash !== id) {
      window.location.hash = id;
    }

    const everywhereHeader = document.querySelector(".everywhere-header");
    const headerHeight = everywhereHeader.clientHeight || 0;
    const aboveSpace = isAarpHeaderHovered ? 70 : 10;

    window.scrollTo(
      0,
      document.querySelector(id).offsetTop - headerHeight - aboveSpace
    );
  };

  componentDidMount() {
    this.setState({ url: window.location.href });
    if (!isServer) {
      BadgeService.updateBadgeStatus();
      const anchor = window.location.hash;
      if (anchor) {
        this.goTo(anchor)();
      }
    }
  }

  render() {
    const meta = PagesSEO.find((s) => s.Arena5Slug === Pages.Help);
    //! If update this component
    //! Change the pageChangedDate for new current date
    const pageChangedDate = new Date("2022-12-07T10:52:01.670Z").toISOString();
    const schemaData = helpPageStructuredData(
      this.state.url,
      meta.MetaHTMLTitle,
      meta.MetaHTMLDescription,
      pageChangedDate
    );

    return (
      <React.Fragment>
        <Helmet
          htmlAttributes={{ lang: "en-US" }}
          title={meta.MetaHTMLTitle}
          meta={[
            {
              name: "description",
              content: meta.MetaHTMLDescription,
            },
            {
              name: "robots",
              content: "index,follow",
            },
            {
              property: "og:description",
              content: meta.OgDescription,
            },
            {
              property: "og:title",
              content: meta.OgTitle,
            },
          ]}
          link={[{ rel: "canonical", href: environment.PROD_HOST + "/help" }]}
        >
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
        </Helmet>
        <GridRow className={styles.contentRow}>
          <ContentColumn size={[8, 8, 12]} direction="column">
            <Block paddingB="xl">
              <Block paddingB="xl" paddingT="md">
                <H1>AARP Games Help Center</H1>
              </Block>

              <Block>
                <H3>About AARP Games</H3>
                <QuestionsContainer>
                  <Block>
                    This AARP Games site is designed to take your game
                    experience to the next level!
                    <br />
                    Enjoy these features, created with you in mind:
                    <Divider />
                    <ul>
                      <li>
                        <strong>Mobile Access</strong> - This site, and all
                        games on it are mobile-friendly, so you can play on most
                        Android and iOS smartphones and tablets.
                      </li>
                      <li>
                        <strong>Game Recommendations</strong> - Discover new
                        games and keep the fun going with personalized game
                        recommendations.
                      </li>
                      <li>
                        <strong>Game Screen Size</strong> – Choose from two game
                        sizes on every game: Standard and Large. Toggle between
                        the two to see what works best for you.
                      </li>
                      <li>
                        <strong>Site Speed</strong> - Get to your games faster,
                        while other page elements load in the background.
                      </li>
                    </ul>
                  </Block>
                </QuestionsContainer>
              </Block>

              <Block>
                <H3>Frequently Asked Questions</H3>
                <QuestionsContainer>
                  <Link onClick={this.goTo("#q1_1")}>
                    What is Theater Mode and how do I access it?
                  </Link>
                  <Link onClick={this.goTo("#q1_2")}>
                    Can I play AARP Games on my mobile phone or tablet?
                  </Link>
                  <Link onClick={this.goTo("#q1_3")}>
                    What are the system requirements to play AARP Games?
                  </Link>
                  <Link onClick={this.goTo("#q1_4")}>
                    How do I find the instructions on a game?
                  </Link>
                  <Link onClick={this.goTo("#q1_5")}>
                    How do I save my score?
                  </Link>
                  <Link onClick={this.goTo("#q1_6")}>How do I log in?</Link>
                  <Link onClick={this.goTo("#q1_7")}>
                    How do I register on AARP.org?
                  </Link>
                  <Link onClick={this.goTo("#q1_8")}>
                    What if I forgot my password?
                  </Link>
                  <Link onClick={this.goTo("#q1_9")}>
                    My game isn’t loading. What can I do?
                  </Link>
                  <Link onClick={this.goTo("#q1_10")}>
                    My games seem slow. What can I do?
                  </Link>
                </QuestionsContainer>
              </Block>

              <Block>
                <H3>AARP Rewards Category on Games</H3>
                <QuestionsContainer>
                  <Link onClick={this.goTo("#q3_1")}>
                    What is the AARP Rewards on Games category?
                  </Link>
                  <Link onClick={this.goTo("#q3_2")}>
                    Who can earn AARP Rewards points on Games?
                  </Link>
                  <Link onClick={this.goTo("#q3_3")}>
                    How many AARP Rewards points can I earn playing games?
                  </Link>
                  <Link onClick={this.goTo("#q3_4")}>
                    How often can I earn AARP Rewards points playing games?
                  </Link>
                  <Link onClick={this.goTo("#q3_5")}>
                    Why do certain games qualify for AARP Rewards points and
                    others don’t?
                  </Link>
                  <Link onClick={this.goTo("#q3_6")}>
                    What is the difference between my game score and AARP
                    Rewards points?
                  </Link>
                  <Link onClick={this.goTo("#q3_7")}>
                    How do I earn AARP Rewards points on games?
                  </Link>
                </QuestionsContainer>
              </Block>

              <Block>
                <H3>Contact Us</H3>
                <QuestionsContainer>
                  <Link onClick={this.goTo("#q2_1")}>
                    Still have questions?
                  </Link>
                </QuestionsContainer>
              </Block>

              <HR />

              <Block>
                <H3>Frequently Asked Questions</H3>

                <QuestionsContainer id="q1_1">
                  <FAQItem>
                    What is Theater Mode and how do I access it?
                  </FAQItem>
                  <Block>
                    Theater Mode provides a full-width game play area on desktop
                    and laptop, while conveniently keeping the browser bar open
                    to navigate back.
                  </Block>
                  <Divider />
                  <Block>
                    Activating Theater Mode is simple. First, ensure you're
                    logged in, and your browser Zoom level is set at 100% or
                    less. Then navigate to the game of your choice and click the
                    "Play Now" button as usual. If your game offers Theater
                    Mode, you'll see a special square icon in the lower-right
                    corner of the game. Simply click that, and you're ready to
                    play! Click the icon again to return to standard size.
                  </Block>
                  <Divider />
                  <Block>
                    It's best to login to access Theater Mode before you start a
                    game to save your game progress.
                  </Block>
                  <Divider />
                </QuestionsContainer>

                <QuestionsContainer id="q1_2">
                  <FAQItem>
                    Can I play AARP Games on my mobile phone or tablet?
                  </FAQItem>
                  <div>
                    All AARP games are built using WebGL technology, which is
                    accessible on all devices – desktop computers, laptops, and
                    most Android and iOS mobile phones and tablets. At this time
                    Amazon Kindle Fire and the Barnes & Noble Nook tablets are
                    not supported. Please see full system requirements for
                    detailed information.
                  </div>
                </QuestionsContainer>

                <QuestionsContainer id="q1_3">
                  <FAQItem>
                    What are the system requirements to play AARP Games?
                  </FAQItem>
                  <div>
                    For the most enjoyable experience when playing AARP Games,
                    make sure your computer/device of choice supports the
                    minimum system requirements as follows:
                    <Divider />
                    <u>Hardware requirements:</u>
                    <div>1.5 GHz processor</div>
                    <div>Windows OS: Windows 10</div>
                    <div>macOS: Big Sur (11.6.4)</div>
                    <div>Resolution: 1024x768</div>
                    <div>2 Mb/s internet connection</div>
                    <div>JavaScript enabled</div>
                    <Divider />
                    <u>Desktop browsers:</u>
                    <div>Chrome 104</div>
                    <div>Firefox 102</div>
                    <div>Edge 104</div>
                    <div>Safari 14</div>
                    <Divider />
                    <u>Mobile devices browsers:</u>
                    <div>Chrome 104</div>
                    <div>Safari 14</div>
                    <Divider />
                    <u>Mobile devices OS:</u>
                    <div>iOS 14</div>
                    <div>Android 10 / Android 11</div>
                  </div>
                </QuestionsContainer>

                <QuestionsContainer id="q1_4">
                  <FAQItem>How do I find the instructions on a game?</FAQItem>
                  <div>
                    On many of the AARP’s games, instructions are in a menu
                    rather than on the opening screen. Look in the corners of
                    the game screen for a question mark icon, a settings icon
                    (looks like a gear), an "i" information icon or a menu icon
                    (three horizontal line stacked together or two vertical
                    lines stacked together).
                  </div>
                </QuestionsContainer>

                <QuestionsContainer id="q1_5">
                  <FAQItem>How do I save my score?</FAQItem>
                  <div>
                    You may save your score to the personal score box and
                    leaderboard as long as you are registered and logged in to
                    AARP.org.
                  </div>
                </QuestionsContainer>

                <QuestionsContainer id="q1_6">
                  <FAQItem>How do I log in?</FAQItem>
                  <div>
                    You will be asked if you want to save your score at the end
                    of the game and prompted to log in. You can also find the
                    Login prompt in the top right of the red header bar. Type in
                    the email address and password you used to register with
                    AARP.org and select "Log In".
                    <br />
                    You can confirm that you are logged in by looking for your
                    name in on the right side of the red header bar at the top
                    of the page.
                  </div>
                </QuestionsContainer>

                <QuestionsContainer id="q1_7">
                  <FAQItem>How do I register on AARP.org?</FAQItem>
                  <div>
                    If you aren’t yet registered on AARP.org, you may find the
                    “Register” prompt in the top right of the red header bar.
                    You do not have to be an AARP member to register on AARP.org
                    and save your scores.
                  </div>
                </QuestionsContainer>

                <QuestionsContainer id="q1_8">
                  <FAQItem>What if I forgot my password?</FAQItem>
                  <div>
                    If you don't remember your AARP.org password, click "Forgot
                    your password?" on the login page and follow the prompts to
                    reset it.
                  </div>
                </QuestionsContainer>

                <QuestionsContainer id="q1_9">
                  <FAQItem>My game isn’t loading. What can I do?</FAQItem>
                  <Block>
                    You may need to clear your cookies and update your browser
                    to the latest version.
                    <br />
                    Instruction to clear your cookies and browser cache:
                    <ul>
                      <li>
                        <strong>Chrome</strong>:&nbsp;
                        <ExLink href="https://support.google.com/accounts/answer/32050" />
                      </li>
                      <li>
                        <strong>FireFox</strong>:&nbsp;
                        <ExLink href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox" />
                      </li>
                      <li>
                        <strong>Edge</strong>:&nbsp;
                        <ExLink href="https://support.microsoft.com/en-us/help/4468242/microsoft-edge-browsing-data-and-privacy-microsoft-privacy" />
                      </li>
                      <li>
                        <strong>Safari</strong>:&nbsp;
                        <ExLink href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" />
                      </li>
                      <li>
                        <strong>Safari (iPhone, iPad, iPod Touch)</strong>
                        :&nbsp;
                        <ExLink href="https://support.apple.com/en-us/HT201265" />
                      </li>
                    </ul>
                    <Divider />
                    Instructions to update your browser:
                    <ul>
                      <li>
                        <strong>Chrome</strong>:&nbsp;
                        <ExLink href="https://support.google.com/chrome/answer/95414" />
                      </li>
                      <li>
                        <strong>FireFox</strong>:&nbsp;
                        <ExLink href="https://support.mozilla.org/en-US/kb/update-firefox-latest-version" />
                      </li>
                      <li>
                        <strong>Edge</strong>:&nbsp;
                        <ExLink href="https://support.microsoft.com/en-us/help/4027667/windows-10-update" />
                      </li>
                      <li>
                        <strong>Safari</strong>:&nbsp;
                        <ExLink href="https://support.apple.com/en-us/HT204416" />
                      </li>
                    </ul>
                  </Block>
                </QuestionsContainer>

                <QuestionsContainer id="q1_10">
                  <FAQItem>My games seem slow. What can I do?</FAQItem>
                  <Block>
                    If your browser is up to date and your cookies are cleared,
                    the problem may be related to the speed of your internet
                    connection. You can check your internet speed here:{" "}
                    <ExLink href="http://www.speedtest.net" />
                    &nbsp;. If you speed is less than 2 MB/s, please contact
                    your internet service provider. They should help you
                    investigate your connectivity issues.
                  </Block>
                </QuestionsContainer>
              </Block>

              <HR />

              <Block>
                <H3>AARP Rewards Category on Games</H3>

                <QuestionsContainer id="q3_1">
                  <FAQItem>What is the AARP Rewards on Games category?</FAQItem>
                  <Block>
                    The{" "}
                    <LinkMore href="/category/rewards-games?intcmp=AM-A5GAM-HELP-CTGRWDS">
                      <strong>AARP Rewards category</strong>
                    </LinkMore>
                    &nbsp; includes games that qualify for earning AARP Rewards
                    points.
                    <Divider />
                    AARP Rewards is AARP’s loyalty program that rewards you for
                    a life well played. You can earn points by completing
                    certain activities throughout AARP.org.
                    <Divider />
                    <LinkMore href="https://www.aarp.org/rewards/?intcmp=AM-A5GAM-HELP-REWARDS">
                      <strong>Learn more about AARP Rewards &gt;</strong>
                    </LinkMore>
                  </Block>
                </QuestionsContainer>

                <QuestionsContainer id="q3_2">
                  <FAQItem>Who can earn AARP Rewards points on Games?</FAQItem>
                  <Block>
                    Anyone who is an active AARP Rewards participant can qualify
                    to earn points. Not part of AARP Rewards yet?&nbsp;
                    <LinkMore href="https://www.aarp.org/rewards/?intcmp=AM-A5GAM-HELP-REWARDS">
                      <strong>Get started for free.</strong>
                    </LinkMore>
                    <Divider />
                    To access AARP Members only games, including Let’s
                    Crossword, you will need an AARP Membership. (Don’t forget,
                    AARP Members can get up to 50% more AARP Rewards points!)
                  </Block>
                </QuestionsContainer>

                <QuestionsContainer id="q3_3">
                  <FAQItem>
                    How many AARP Rewards points can I earn playing games?{" "}
                  </FAQItem>
                  <Block>
                    You can earn a different amount of points for each game.
                    Look for the AARP Rewards badge on the game for the point
                    value indicator.
                  </Block>
                </QuestionsContainer>

                <QuestionsContainer id="q3_4">
                  <FAQItem>
                    How often can I earn AARP Rewards points playing games?{" "}
                  </FAQItem>
                  <Block>You can earn points once a day for each game.</Block>
                </QuestionsContainer>

                <QuestionsContainer id="q3_5">
                  <FAQItem>
                    Why do certain games qualify for AARP Rewards points and
                    others don’t?
                  </FAQItem>
                  <Block>
                    AARP Rewards awards users for staying active, supporting
                    strong brain health, and creating social connections. Games
                    badged for AARP Rewards reflect this ideal.
                  </Block>
                </QuestionsContainer>

                <QuestionsContainer id="q3_6">
                  <FAQItem>
                    What is the difference between my game score and AARP
                    Rewards points?
                  </FAQItem>
                  <Block>
                    Your game score is what you achieve by playing any game on
                    games.aarp.org. Each time you play a game, you will see your
                    score when you reach the game end screen. Your daily and
                    personal best scores will also show in your personal score
                    box below the game when you’re logged in. If you qualify for
                    the game’s leaderboard, you will see your username and the
                    best daily, weekly or monthly score there, too. These scores
                    help you achieve your personal best, but do not equate to
                    any monetary value.
                    <Divider />
                    AARP Rewards points are points that you earn by completing
                    AARP Rewards activities throughout AARP.org, including games
                    marked with the AARP Rewards badge. When you’re logged in
                    and AARP Rewards activated, your total AARP Rewards points
                    are displayed in the red bar at the top of AARP.org. You can
                    redeem these points towards points-only offers, sweepstakes,
                    discounted gift cards and more at AARP.org/Rewards.
                  </Block>
                </QuestionsContainer>

                <QuestionsContainer id="q3_7">
                  <FAQItem>How do I earn AARP Rewards points on games?</FAQItem>
                  <Block>
                    To earn AARP Rewards points on games, you must first be
                    registered on aarp.org and activated on AARP Rewards.
                    <Divider />
                    Then, play and complete a game under the&nbsp;
                    <LinkMore href="/category/rewards-games?intcmp=AM-A5GAM-HELP-CTGRWDS">
                      <strong>AARP Rewards category on AARP Games.</strong>
                    </LinkMore>
                    Once you complete the crossword puzzle, click “Submit Score”
                    to submit your game and earn points. Remember, you can only
                    earn points once for each game per day.
                    <Divider />
                    Give it your best shot and aim for a top score! Keep in mind
                    that using the puzzles’ hint features in the menu, like the
                    ‘Reveal’ function or the ‘Show Errors’ toggle in the ‘on’
                    position, could affect your score.
                    <Divider />
                    Don’t forget, to get your AARP Rewards points, you must hit
                    ‘Submit Score’ when you reach the end of the game or quit
                    the game from the menu, instead of browsing out.
                    <Divider />
                    The points that you earn from these games also count towards
                    your AARP Reward point daily limits.
                    <Divider />
                    To learn about more ways to earn AARP Rewards points,
                    visit&nbsp;
                    <LinkMore href="https://www.aarp.org/rewards/earn/?intcmp=AM-A5GAM-HELP-REWARDS">
                      <strong>AARP.org/Rewards/Earn</strong>
                    </LinkMore>
                    <Divider />
                    Still have questions about AARP Rewards?
                    <Divider />
                    Get support by phone from 7am to 11pm ET weekdays by calling
                    866-451-6305, get AARP Help online at&nbsp;
                    <ExLink href="https://www.help.aarp/s/topic/0TO4P000000If6pWAC/aarp-rewards" />
                    &nbsp; or look for answers in our community forums and help
                    section.
                    <Divider />
                    Ready to have some fun?
                    <br />
                    <LinkMore href="/category/rewards-games?intcmp=AM-A5GAM-HELP-CTGRWDS">
                      <strong>Play Now &gt;&gt;</strong>
                    </LinkMore>
                  </Block>
                </QuestionsContainer>
              </Block>

              <HR />

              <Block>
                <H3>Contact Us</H3>
                <QuestionsContainer id="q2_1">
                  <TextLikeLink>
                    {" "}
                    Still have questions about games? <Freshdesk size="s" />
                  </TextLikeLink>
                  <Block>
                    <Text>
                      For other questions about AARP, including questions about
                      your account, membership, advertisements, other site
                      content or AARP stance on issues, please visit&nbsp;
                      <LinkMore href="https://www.aarp.org/about-aarp/contact-us/?intcmp=AM-A5GAM-HELP">
                        <strong>AARP Customer Care</strong>
                      </LinkMore>
                      &nbsp;or call customer service at 1 (888) 687-2277.
                    </Text>
                  </Block>
                </QuestionsContainer>
              </Block>
            </Block>

            <HR />

            <Divider />
            <Block>
              <QuestionsContainer id="atari">
                ©2021 Atari Interactive, Inc. Atari and Atari logo are
                registered trademarks owned by Atari Interactive, Inc. All
                individual Atari game names and logos are trademarks or
                registered trademarks of Atari Interactive, Inc. or its
                affiliates. Used under license.
              </QuestionsContainer>
            </Block>
            <Divider />
          </ContentColumn>

          <GridCell size={[4, 4, 12]} direction="column">
            <RightRail mode="Help" />
          </GridCell>
        </GridRow>
      </React.Fragment>
    );
  }
}

const ContentColumn = (props: any) => (
  <GridCell className={styles.contentColumn} {...props} />
);
const Link = (props: any) => <a className={styles.link} {...props} />;
const LinkMore = (props: any) => <a className={styles.linkMore} {...props} />;
const TextLikeLink = (props: any) => (
  <span className={styles.textLikeLink} {...props} />
);
const FAQItem = (props: any) => {
  return <Text {...props} className={styles.faqItem} />;
};
const QuestionsContainer = (props: any) => (
  <div className={styles.questionsContainer} {...props} />
);
const HR = (props: any) => <hr className={styles.hr} {...props} />;
const Divider = (props: any) => <div className={styles.divider} {...props} />;
export const ExLink = (props: any) => (
  <a href={props.href} className={styles.externalLink} children={props.href} />
);
